import React from 'react';
import {
  Alert,
  Breadcrumb,
  Button,
  Dropdown,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  MoreOutlined,
  ArrowLeftOutlined,
  DeleteOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { GoodsProps } from '../../api/Goods';

const { Title, Text } = Typography;

type Props = {
  data: GoodsProps;
  mpLinked: number;
  mpTotal: number;
  mpArchived?: number;
  sideLoading?: boolean;
  onEdit: () => void;
  onEditBasics?: () => void;
  onDelete?: () => void;
  onRestore?: () => void;
  deleting?: boolean;
  alerts?: React.ReactNode;
};

export const ProductHeader = ({
  data,
  mpLinked,
  mpTotal,
  mpArchived = 0,
  sideLoading,
  onEdit,
  onEditBasics,
  onDelete,
  onRestore,
  deleting,
  alerts,
}: Props) => {
  const title = data.name?.trim() || 'Без названия';
  const fbs = !!data.product_remnants?.show_for_distribution;
  const missingName = !data.name?.trim();
  const missingBrand = !data.brand?.name?.trim();
  const isDeleted = !!data.deleted_at;

  const menuItems = [
    {
      key: 'fill',
      label: 'Заполнить данные',
      onClick: onEditBasics || onEdit,
      disabled: isDeleted,
    },
    {
      key: 'list',
      label: <Link to="/settings/goods">К списку товаров</Link>,
    },
    ...(isDeleted && onRestore
      ? [
          {
            key: 'restore',
            label: 'Восстановить',
            icon: <UndoOutlined />,
            onClick: onRestore,
          },
        ]
      : onDelete
        ? [
            {
              key: 'delete',
              label: 'Удалить',
              icon: <DeleteOutlined />,
              danger: true,
              onClick: onDelete,
            },
          ]
        : []),
  ];

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: '#fff',
        borderBottom: '1px solid #f0f0f0',
        margin: '0 -12px 16px',
        padding: '12px 16px 16px',
      }}
    >
      <Breadcrumb
        style={{ marginBottom: 8 }}
        items={[
          {
            title: (
              <Link to="/settings/goods">
                <ArrowLeftOutlined /> Товары
              </Link>
            ),
          },
          { title: data.article },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 16,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <Title
            level={3}
            style={{
              margin: 0,
              color: missingName ? '#ad6800' : undefined,
            }}
          >
            {title}
          </Title>
          <Space wrap size={[8, 4]} style={{ marginTop: 6 }}>
            <Text type="secondary">{data.article}</Text>
            <Text type="secondary">·</Text>
            <Tag color={fbs ? 'success' : 'default'}>
              FBS {fbs ? 'включён' : 'выключен'}
            </Tag>
            {sideLoading ? (
              <Tag>
                <Spin size="small" style={{ marginRight: 6 }} />
                Связи с МП…
              </Tag>
            ) : (
              <Tag color={mpLinked === 0 ? 'warning' : 'processing'}>
                {mpLinked === 0
                  ? 'Не связан с МП'
                  : `${mpLinked} из ${mpTotal} МП`}
              </Tag>
            )}
            {!sideLoading && mpArchived > 0 && (
              <Tag>Удалён на МП: {mpArchived}</Tag>
            )}
            {data.is_combo && <Tag>Комбо</Tag>}
            {data.deleted_at && <Tag color="error">Удалён</Tag>}
          </Space>
        </div>
        <Space>
          {!isDeleted && (
            <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
              Редактировать
            </Button>
          )}
          {isDeleted && onRestore ? (
            <Button
              icon={<UndoOutlined />}
              onClick={onRestore}
              loading={deleting}
            >
              Восстановить
            </Button>
          ) : (
            onDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={onDelete}
                loading={deleting}
              >
                Удалить
              </Button>
            )
          )}
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        </Space>
      </div>
      {(missingName || missingBrand) && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          message="У товара не заполнены название и бренд"
          description={
            <span>
              {[missingName && 'название', missingBrand && 'бренд']
                .filter(Boolean)
                .join(' и ')}{' '}
              отсутствуют — заполните, чтобы проще находить товар и матчить с МП.
            </span>
          }
          action={
            <Button size="small" type="primary" onClick={onEditBasics || onEdit}>
              Заполнить данные
            </Button>
          }
        />
      )}
      {alerts}
    </div>
  );
};
